import React from 'react';

import {
    MFA_ALL,
    ORG_ROLE_ADMIN,
    ORG_ROLE_USER,
    PROJECT_NONE,
    STATUS_ACTIVE,
    STATUS_DEACTIVATED,
    STATUS_INVITE_EXPIRED,
    STATUS_INVITED,
} from './organisationUserFormat';

import SelectInput from 'CommonComponents/SelectInput';
import TextButton from 'CommonComponents/TextButton';

import SearchIcon from 'Assets/icons/search.svg';

const LAST_LOGIN_OPTIONS = () => [
    { value: 'any', label: $t('Last login: any time') },
    { value: '7d', label: $t('Last login: last 7 days') },
    { value: '30d', label: $t('Last login: last 30 days') },
    { value: 'over_30d', label: $t('Last login: over 30 days ago') },
    { value: 'never', label: $t('Last login: never') },
];

// A native select sizes itself to its longest option; the wrapper keeps the
// row of filters on one line instead, at the design's widths and type.
export function Filter({ id, label, value, widthClassName, onChange, children }) {
    return (
        <div
            className={`${widthClassName} [&_select]:tw-w-full [&_select]:tw-truncate [&_select]:!tw-text-0.5sm [&_select]:!tw-font-normal [&_select]:!tw-rounded-full [&_select]:!tw-ps-3 [&_select]:!tw-pe-8`}
        >
            <SelectInput
                id={id}
                name={id}
                aria-label={label}
                value={value ?? ''}
                onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)}
            >
                {children}
            </SelectInput>
        </div>
    );
}

// Filled like the selects beside it, as on the project Activity tab.
export function SearchNameOrEmail({ value, onChange }) {
    return (
        <label className="tw-w-60 tw-h-[37px] tw-mb-0 tw-px-3.5 tw-flex tw-items-center tw-gap-x-2 tw-rounded-full tw-bg-grey/50 tw-text-charcoal-50">
            <SearchIcon className="tw-w-4 tw-h-4 tw-shrink-0 tw-text-charcoal-50" />
            <input
                type="text"
                className="table__search-input tw-w-full !tw-min-w-0 !tw-text-sm !tw-font-light placeholder:!tw-text-charcoal-50"
                placeholder={$t('Search name or email')}
                aria-label={$t('Search name or email')}
                value={value}
                onChange={(event) => onChange(event.target.value)}
            />
        </label>
    );
}

export default function OrganisationUserToolbar({ query, searchInput, projects, shown, total, onSearchChange, onChange, onClear }) {
    return (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
            <SearchNameOrEmail value={searchInput} onChange={onSearchChange} />

            <Filter
                id="org_users_project"
                widthClassName="tw-w-[228px]"
                label={$t('Project')}
                value={query.project}
                onChange={(project) => onChange({ project })}
            >
                <option value="">{$t('All projects')}</option>
                <option value={PROJECT_NONE}>{$t('Not on any project')}</option>
                {projects.map((project) => (
                    <option key={project.value} value={project.value}>
                        {project.label}
                    </option>
                ))}
            </Filter>

            <Filter
                id="org_users_org_role"
                widthClassName="tw-w-[166px]"
                label={$t('Organisation role')}
                value={query.org_role}
                onChange={(orgRole) => onChange({ org_role: orgRole })}
            >
                <option value="">{$t('All organisation roles')}</option>
                <option value={ORG_ROLE_ADMIN}>{$t('Organisation Admin')}</option>
                <option value={ORG_ROLE_USER}>{$t('Organisation user')}</option>
            </Filter>

            <Filter id="org_users_status" widthClassName="tw-w-[118px]" label={$t('Status')} value={query.status} onChange={(status) => onChange({ status })}>
                <option value="">{$t('All statuses')}</option>
                <option value={STATUS_ACTIVE}>{$t('Active')}</option>
                <option value={STATUS_INVITED}>{$t('Invited')}</option>
                <option value={STATUS_INVITE_EXPIRED}>{$t('Invite expired')}</option>
                <option value={STATUS_DEACTIVATED}>{$t('Deactivated')}</option>
            </Filter>

            <Filter
                id="org_users_last_login"
                widthClassName="tw-w-[158px]"
                label={$t('Last login')}
                value={query.last_login}
                onChange={(lastLogin) => onChange({ last_login: lastLogin ?? 'any' })}
            >
                {LAST_LOGIN_OPTIONS().map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </Filter>

            <Filter id="org_users_mfa" widthClassName="tw-w-[98px]" label={$t('MFA')} value={query.mfa} onChange={(mfa) => onChange({ mfa: mfa ?? MFA_ALL })}>
                <option value={MFA_ALL}>{$t('MFA: all')}</option>
                <option value="set_up">{$t('MFA: set up')}</option>
                <option value="not_set_up">{$t('MFA: not set up')}</option>
            </Filter>

            <TextButton variant="primary" onClick={onClear}>
                {$t('Clear')}
            </TextButton>

            <p className="tw-ms-auto tw-mb-0 tw-text-xs tw-text-charcoal-50">{$t('{{shown}} of {{total}} users', { shown: shown ?? 0, total: total ?? 0 })}</p>
        </div>
    );
}
